import React, { useEffect, useRef, useState } from 'react';
import Guacamole from 'guacamole-common-js';

const GuacamoleDisplay = ({ sessionId, agentId, onError, onDisconnect }) => {
  const displayRef = useRef(null);
  const [client, setClient] = useState(null);
  const [connectionState, setConnectionState] = useState('connecting');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!displayRef.current || !agentId) return;

    try {
      // Create WebSocket tunnel to backend
      // Use relative URL so it goes through the same domain (Kubernetes ingress will route to backend)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const tunnelUrl = `${protocol}//${host}/api/remote/ws/${agentId}/${sessionId}`;

      console.log('🔌 Connecting to Guacamole tunnel:', tunnelUrl);

      // Create Guacamole tunnel
      const tunnel = new Guacamole.WebSocketTunnel(tunnelUrl);

      // Create Guacamole client
      const guacClient = new Guacamole.Client(tunnel);

      // Get display element
      const display = guacClient.getDisplay().getElement();
      displayRef.current.innerHTML = '';
      displayRef.current.appendChild(display);

      // Handle connection state changes
      guacClient.onstatechange = (state) => {
        console.log('🔄 Guacamole state changed:', state);

        switch (state) {
          case Guacamole.Client.CONNECTING:
            setConnectionState('connecting');
            break;
          case Guacamole.Client.CONNECTED:
            setConnectionState('connected');
            break;
          case Guacamole.Client.DISCONNECTING:
            setConnectionState('disconnecting');
            break;
          case Guacamole.Client.DISCONNECTED:
            setConnectionState('disconnected');
            if (onDisconnect) onDisconnect();
            break;
          case Guacamole.Client.IDLE:
            setConnectionState('idle');
            break;
          default:
            console.warn('Unknown state:', state);
        }
      };

      // Handle errors
      guacClient.onerror = (error) => {
        console.error('❌ Guacamole error:', error);
        const errMsg = error?.message || 'Connection error occurred';
        setErrorMessage(errMsg);
        setConnectionState('error');
        if (onError) onError(errMsg);
      };

      // Create mouse handler
      const mouse = new Guacamole.Mouse(displayRef.current);
      mouse.onmousedown = mouse.onmouseup = mouse.onmousemove = (mouseState) => {
        guacClient.sendMouseState(mouseState);
      };

      // Create keyboard handler
      const keyboard = new Guacamole.Keyboard(document);
      keyboard.onkeydown = (keysym) => {
        guacClient.sendKeyEvent(1, keysym);
      };
      keyboard.onkeyup = (keysym) => {
        guacClient.sendKeyEvent(0, keysym);
      };

      // Connect to remote desktop
      guacClient.connect();

      setClient(guacClient);

      // Cleanup on unmount
      return () => {
        if (keyboard) keyboard.reset();
        if (guacClient) {
          guacClient.disconnect();
        }
      };
    } catch (error) {
      console.error('❌ Failed to initialize Guacamole client:', error);
      setErrorMessage(error.message || 'Failed to initialize connection');
      setConnectionState('error');
      if (onError) onError(error.message);
    }
  }, [agentId, sessionId, onError, onDisconnect]);

  // Handle window resize
  useEffect(() => {
    if (!client) return;

    const handleResize = () => {
      try {
        const width = window.innerWidth;
        const height = window.innerHeight - 100; // Account for control bars
        client.sendSize(width, height);
      } catch (error) {
        console.error('Error resizing:', error);
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial resize
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [client]);

  return (
    <div className="relative w-full h-full bg-black">
      {/* Connection status overlay */}
      {connectionState !== 'connected' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90 z-10">
          <div className="text-center">
            {connectionState === 'connecting' && (
              <>
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-white text-lg">Connecting to remote desktop...</p>
                <p className="text-gray-400 text-sm mt-2">Establishing secure connection</p>
              </>
            )}
            {connectionState === 'disconnecting' && (
              <>
                <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-white text-lg">Disconnecting...</p>
              </>
            )}
            {connectionState === 'disconnected' && (
              <>
                <div className="text-yellow-500 text-5xl mb-4">⚠️</div>
                <p className="text-white text-lg">Disconnected</p>
                <p className="text-gray-400 text-sm mt-2">The session has ended</p>
              </>
            )}
            {connectionState === 'error' && (
              <>
                <div className="text-red-500 text-5xl mb-4">❌</div>
                <p className="text-white text-lg">Connection Error</p>
                <p className="text-gray-400 text-sm mt-2">{errorMessage || 'Failed to connect'}</p>
                <p className="text-gray-500 text-xs mt-4">
                  Note: Guacamole server (guacd) must be running on the backend server
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Guacamole display container */}
      <div 
        ref={displayRef} 
        className="w-full h-full overflow-hidden"
        style={{ cursor: 'default' }}
      />

      {/* Connection indicator */}
      {connectionState === 'connected' && (
        <div className="absolute top-2 right-2 bg-green-500/90 text-white text-xs px-3 py-1 rounded-full flex items-center gap-2">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          Connected
        </div>
      )}
    </div>
  );
};

export default GuacamoleDisplay;
